import type { CanonicalTweet } from '@sweetistics/contracts/tweets-canonical';
import { type QueryClient, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { translateBatchLoadError } from '../errors';
import { parseCanonicalTweet } from '../parsing';

export interface BatchLoadTweetsResult {
  tweets?: Record<string, unknown>;
}

export type BatchLoadTweetsFn = (ids: readonly string[]) => Promise<BatchLoadTweetsResult>;

type QueryClientLike = Pick<QueryClient, 'getQueryData' | 'setQueryData'>;

export interface CreateUseTweetByIdDependencies {
  /**
   * Batched fetcher that returns a map keyed by tweet id. Consumers can wire this to
   * tRPC or any HTTP client; the hook stays agnostic.
   */
  batchLoadTweets: BatchLoadTweetsFn;
  /**
   * Accessor for the current React Query client. We accept a getter instead of the
   * instance so the hook plays nicely with multiple providers (e.g., dev tools, tests).
   */
  useQueryClient: () => QueryClientLike;
}

export interface UseTweetByIdOptions {
  enabled?: boolean;
}

export type UseTweetByIdHook = (
  tweetId: string | null,
  options?: UseTweetByIdOptions
) => UseQueryResult<CanonicalTweet>;

const TWEET_QUERY_KEY = 'tweet';
const STALE_TIME_MS = 5 * 60 * 1000;
const GC_TIME_MS = 30 * 60 * 1000;

// Factory that wires our batch tweet loader into a React Query hook with caching.
export function createUseTweetById(deps: CreateUseTweetByIdDependencies): UseTweetByIdHook {
  const { useQueryClient, batchLoadTweets } = deps;

  return (tweetId, options) => {
    const queryClient = useQueryClient();
    const queryKey = [TWEET_QUERY_KEY, tweetId] as const;

    const result: UseQueryResult<CanonicalTweet> = useQuery<CanonicalTweet>({
      queryKey,
      staleTime: STALE_TIME_MS,
      gcTime: GC_TIME_MS,
      retry: false,
      enabled: Boolean(tweetId) && (options?.enabled ?? true),
      queryFn: async () => {
        if (tweetId == null) {
          throw new Error('Tweet id is required');
        }

        const cachedTweet = queryClient.getQueryData<CanonicalTweet>(queryKey);
        if (cachedTweet) {
          return cachedTweet;
        }

        try {
          const response: BatchLoadTweetsResult = await batchLoadTweets([tweetId]);
          const tweets = response.tweets ?? {};
          let resolvedTweet: CanonicalTweet | null = null;

          for (const [id, raw] of Object.entries(tweets)) {
            if (!raw) {
              continue;
            }
            const parsed = parseCanonicalTweet(raw);
            queryClient.setQueryData<CanonicalTweet>([TWEET_QUERY_KEY, id], parsed);
            if (id === tweetId) {
              resolvedTweet = parsed;
            }
          }

          if (!resolvedTweet) {
            throw new Error('Tweet not found');
          }

          return resolvedTweet;
        } catch (error) {
          throw translateBatchLoadError(error);
        }
      },
    });

    // TanStack currently infers the DefinedUseQueryResult overload for this configuration,
    // but consumers of the factory expect the broader UseQueryResult contract so disabled
    // queries (tweetId === null) still type-check.
    return result;
  };
}
